import { DecoratorNode } from "lexical";
import React, { useState, useRef, useEffect } from "react";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { useLexicalNodeSelection } from "@lexical/react/useLexicalNodeSelection";
import { mergeRegister } from "@lexical/utils";
import {
  $getNodeByKey,
  $getSelection,
  $isNodeSelection,
  CLICK_COMMAND,
  COMMAND_PRIORITY_LOW,
  KEY_DELETE_COMMAND,
  KEY_BACKSPACE_COMMAND
} from "lexical";

function ImageComponent({ src, alt, width, height, nodeKey }) {
  const [editor] = useLexicalComposerContext();
  const [isSelected, setSelected, clearSelection] = useLexicalNodeSelection(nodeKey);
  const [isResizing, setIsResizing] = useState(false);
  const [currentWidth, setCurrentWidth] = useState(width || 300);
  const [currentHeight, setCurrentHeight] = useState(height || null);
  const [showContextMenu, setShowContextMenu] = useState(false);
  const [contextMenuPos, setContextMenuPos] = useState({ x: 0, y: 0 });
  const imageRef = useRef(null);
  const startPos = useRef({ x: 0, y: 0, width: 0, height: 0 });
  const contextMenuRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (contextMenuRef.current && !contextMenuRef.current.contains(event.target)) {
        setShowContextMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    return mergeRegister(
      editor.registerCommand(
        CLICK_COMMAND,
        (event) => {
          if (event.target === imageRef.current) {
            if (!event.shiftKey) {
              clearSelection();
            }
            setSelected(!isSelected);
            return true;
          }
          return false;
        },
        COMMAND_PRIORITY_LOW
      ),
      editor.registerCommand(
        KEY_DELETE_COMMAND,
        (event) => {
          if (isSelected) {
            event.preventDefault();
            editor.update(() => {
              const node = $getNodeByKey(nodeKey);
              if (node) {
                node.remove();
              }
            });
            return true;
          }
          return false;
        },
        COMMAND_PRIORITY_LOW
      ),
      editor.registerCommand(
        KEY_BACKSPACE_COMMAND,
        (event) => {
          if (isSelected) {
            event.preventDefault();
            editor.update(() => {
              const node = $getNodeByKey(nodeKey);
              if (node) {
                node.remove();
              }
            });
            return true;
          }
          return false;
        },
        COMMAND_PRIORITY_LOW
      )
    );
  }, [editor, isSelected, nodeKey, clearSelection, setSelected]);

  const handleContextMenu = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setContextMenuPos({ x: e.clientX, y: e.clientY });
    setShowContextMenu(true);
    setSelected(true);
  };

  const handleChangeImage = () => {
    setShowContextMenu(false);
    fileInputRef.current?.click();
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      editor.update(() => {
        const node = $getNodeByKey(nodeKey);
        if (node) {
          const writable = node.getWritable();
          writable.__src = e.target.result;
        }
      });
    };
    reader.readAsDataURL(file);
    // Réinitialiser l'input pour permettre de sélectionner le même fichier
    event.target.value = '';
  };

  const handleAddCaption = () => {
    const caption = prompt("Entrez la légende de l'image:", alt || "");
    if (caption !== null) {
      editor.update(() => {
        const node = $getNodeByKey(nodeKey);
        if (node) {
          const writable = node.getWritable();
          writable.__alt = caption;
        }
      });
    }
    setShowContextMenu(false);
  };

  const handleMouseDown = (e, corner) => {
    e.preventDefault();
    e.stopPropagation();
    setIsResizing(true);
    
    const rect = imageRef.current.getBoundingClientRect();
    startPos.current = {
      x: e.clientX,
      y: e.clientY,
      width: rect.width,
      height: rect.height
    };

    const handleMouseMove = (moveEvent) => {
      const deltaX = moveEvent.clientX - startPos.current.x;
      const deltaY = moveEvent.clientY - startPos.current.y;
      
      let newWidth = startPos.current.width;
      let newHeight = startPos.current.height;

      if (corner === 'se' || corner === 'ne' || corner === 'e') {
        newWidth = Math.max(50, startPos.current.width + deltaX);
      }
      if (corner === 'sw' || corner === 'se' || corner === 's') {
        newHeight = Math.max(50, startPos.current.height + deltaY);
      }

      setCurrentWidth(newWidth);
      setCurrentHeight(newHeight);

      editor.update(() => {
        const node = $getNodeByKey(nodeKey);
        if (node) {
          node.setWidth(newWidth);
          node.setHeight(newHeight);
        }
      });
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  return (
    <div
      style={{
        position: 'relative',
        display: 'inline-block',
        margin: '8px 0',
        userSelect: 'none'
      }}
    >
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: 'none' }}
        accept="image/*"
        onChange={handleFileChange}
      />
      <img
        ref={imageRef}
        src={src}
        alt={alt || ""}
        data-lexical-node-key={nodeKey}
        onContextMenu={handleContextMenu}
        style={{
          maxWidth: "100%",
          display: "block",
          width: currentWidth ? `${currentWidth}px` : "auto",
          height: currentHeight ? `${currentHeight}px` : "auto",
          cursor: isResizing ? 'nwse-resize' : 'pointer',
          border: isSelected ? '2px solid #2b579a' : '2px solid transparent',
          outline: 'none'
        }}
        draggable={false}
      />
      {alt && (
        <div style={{
          textAlign: 'center',
          fontSize: '12px',
          color: '#666',
          fontStyle: 'italic',
          marginTop: '4px'
        }}>
          {alt}
        </div>
      )}
      {showContextMenu && (
        <div
          ref={contextMenuRef}
          style={{
            position: 'fixed',
            top: `${contextMenuPos.y}px`,
            left: `${contextMenuPos.x}px`,
            backgroundColor: 'white',
            border: '1px solid #ccc',
            borderRadius: '4px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            zIndex: 1000,
            minWidth: '180px',
            padding: '4px 0'
          }}
        >
          <button
            onClick={handleChangeImage}
            style={{
              width: '100%',
              padding: '8px 16px',
              border: 'none',
              backgroundColor: 'transparent',
              textAlign: 'left',
              cursor: 'pointer',
              fontSize: '14px'
            }}
            onMouseEnter={(e) => e.target.style.backgroundColor = '#f0f0f0'}
            onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
          >
            🖼️ Changer l'image
          </button>
          <button
            onClick={handleAddCaption}
            style={{
              width: '100%',
              padding: '8px 16px',
              border: 'none',
              backgroundColor: 'transparent',
              textAlign: 'left',
              cursor: 'pointer',
              fontSize: '14px'
            }}
            onMouseEnter={(e) => e.target.style.backgroundColor = '#f0f0f0'}
            onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
          >
            📝 Ajouter une légende
          </button>
        </div>
      )}
      {isSelected && !isResizing && (
        <>
          {/* Coin bas-droit */}
          <div
            style={{
              position: 'absolute',
              bottom: '-4px',
              right: '-4px',
              width: '12px',
              height: '12px',
              backgroundColor: '#2b579a',
              border: '2px solid white',
              cursor: 'nwse-resize',
              borderRadius: '2px',
              zIndex: 10
            }}
            onMouseDown={(e) => handleMouseDown(e, 'se')}
          />
          {/* Coin bas-gauche */}
          <div
            style={{
              position: 'absolute',
              bottom: '-4px',
              left: '-4px',
              width: '12px',
              height: '12px',
              backgroundColor: '#2b579a',
              border: '2px solid white',
              cursor: 'nesw-resize',
              borderRadius: '2px',
              zIndex: 10
            }}
            onMouseDown={(e) => handleMouseDown(e, 'sw')}
          />
          {/* Coin haut-droit */}
          <div
            style={{
              position: 'absolute',
              top: '-4px',
              right: '-4px',
              width: '12px',
              height: '12px',
              backgroundColor: '#2b579a',
              border: '2px solid white',
              cursor: 'nesw-resize',
              borderRadius: '2px',
              zIndex: 10
            }}
            onMouseDown={(e) => handleMouseDown(e, 'ne')}
          />
          {/* Milieu droite */}
          <div
            style={{
              position: 'absolute',
              top: '50%',
              right: '-4px',
              width: '12px',
              height: '12px',
              backgroundColor: '#2b579a',
              border: '2px solid white',
              cursor: 'ew-resize',
              borderRadius: '2px',
              transform: 'translateY(-50%)',
              zIndex: 10
            }}
            onMouseDown={(e) => handleMouseDown(e, 'e')}
          />
          {/* Milieu bas */}
          <div
            style={{
              position: 'absolute',
              bottom: '-4px',
              left: '50%',
              width: '12px',
              height: '12px',
              backgroundColor: '#2b579a',
              border: '2px solid white',
              cursor: 'ns-resize',
              borderRadius: '2px',
              transform: 'translateX(-50%)',
              zIndex: 10
            }}
            onMouseDown={(e) => handleMouseDown(e, 's')}
          />
        </>
      )}
    </div>
  );
}

export class ImageNode extends DecoratorNode {
  __src;
  __alt;
  __width;
  __height;

  static getType() {
    return "image";
  }

  static clone(node) {
    return new ImageNode(node.__src, node.__alt, node.__width, node.__height, node.__key);
  }

  constructor(src, alt = "", width = null, height = null, key) {
    super(key);
    this.__src = src;
    this.__alt = alt;
    this.__width = width;
    this.__height = height;
  }

  createDOM() {
    const span = document.createElement("span");
    span.style.display = "inline-block";
    return span;
  }

  updateDOM() {
    return false;
  }

  // Rendre le nœud sélectionnable
  isInline() {
    return false;
  }

  // Permet la sélection du nœud
  isKeyboardSelectable() {
    return true;
  }

  decorate() {
    return (
      <ImageComponent
        src={this.__src}
        alt={this.__alt}
        width={this.__width}
        height={this.__height}
        nodeKey={this.getKey()}
      />
    );
  }

  exportJSON() {
    return {
      type: "image",
      version: 1,
      src: this.__src,
      alt: this.__alt,
      width: this.__width,
      height: this.__height
    };
  }

  static importJSON(serializedNode) {
    const { src, alt, width, height } = serializedNode;
    return new ImageNode(src, alt, width, height);
  }
  
  setWidth(width) {
    const self = this.getWritable();
    self.__width = width;
  }

  setHeight(height) {
    const self = this.getWritable();
    self.__height = height;
  }

  getWidth() {
    return this.__width;
  }

  getHeight() {
    return this.__height;
  }
}

export function $createImageNode(src, alt = "", width = null, height = null) {
  return new ImageNode(src, alt, width, height);
}

export function $isImageNode(node) {
  return node instanceof ImageNode;
}